import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, BrandMark, Button, SignInLayout, t, Text } from '@kanbios/ui';
import { api, unwrap } from '../lib/api';
import { safeReturnTo } from '../lib/format';

/**
 * Sign-in (SPEC-001), built as the DSM « SignInLayout › Sign in » frame: the
 * organisation's SSO first, refusals as an Alert above the actions.
 */
export function SignInPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const reason = params.get('reason');
  const code = params.get('code');
  const providers = useQuery({ queryKey: ['providers'], queryFn: async () => unwrap(await api.auth.GET('/session/providers')).providers });
  const start = (provider: string) => window.location.assign(`/api/auth/${provider}/start?returnTo=${encodeURIComponent(returnTo)}`);
  const hasMicrosoft = providers.data?.includes('microsoft');
  const hasDemo = providers.data?.includes('demo');

  return (
    <SignInLayout
      brand={<BrandMark size="lg" name={t('app.brand')} />}
      headline={t('signIn.headline')}
      headlineAccent={t('signIn.headlineAccent')}
      description={t('signIn.description')}
      facts={[
        { icon: 'verified_user', label: t('signIn.fact.sso') },
        { icon: 'key', label: t('signIn.fact.mfa') },
        { icon: 'group', label: t('signIn.fact.groups') },
      ]}
      org={t('signIn.org')}
      title={t('signIn.title')}
      subtitle={t('signIn.subtitle')}
    >
      {reason === 'signed_out' && <Alert tone="info">{t('signIn.signedOut')}</Alert>}
      {reason === 'expired' && <Alert tone="info">{t('signIn.expired')}</Alert>}
      {reason === 'denied' && code === 'no_role' && <Alert tone="neg" title={t('signIn.noRole.title')}>{t('signIn.noRole.hint')}</Alert>}
      {reason === 'denied' && code !== 'no_role' && (
        <Alert tone="neg" title={t('signIn.interrupted.title')}>{t('signIn.interrupted.hint', { ref: params.get('ref') ?? '—' })}</Alert>
      )}
      {hasMicrosoft && <Button block size="lg" icon="domain" onClick={() => start('microsoft')}>{t('signIn.microsoft')}</Button>}
      {hasDemo && <Alert tone="warn" title={t('signIn.demoNotice.title')}>{t('signIn.demoNotice.body')}</Alert>}
      {hasDemo && (
        <Button block size="lg" variant={hasMicrosoft ? 'secondary' : 'primary'} onClick={() => start('demo')}>{t('signIn.demo')}</Button>
      )}
      <Text variant="caption" tone="mute">{t('signIn.help')}</Text>
    </SignInLayout>
  );
}
